import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Code,
  Server,
  AlertTriangle,
  Package,
  Layers,
  Globe,
  Database,
  Shield,
  ExternalLink,
  Filter,
  Download,
  AlertOctagon,
  CheckCircle2,
  Eye,
  Link2,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
} from "../../card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";

// ─── Product SVG Icons ───────────────────────────────────────────────────────
const TechIcons: Record<string, React.ReactNode> = {
  apache: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M17.805 2.012c-.532.1-1.27.394-2.075.883a14.6 14.6 0 0 0-1.825 1.273c-.079-.208-.164-.4-.26-.556-.312-.507-.672-.715-.984-.63-.445.122-.713.81-.753 1.893a14.5 14.5 0 0 0-1.34 1.622c-.117-.323-.247-.614-.393-.847-.38-.608-.793-.874-1.14-.765-.499.157-.764 1.063-.72 2.4a14.5 14.5 0 0 0-.987 1.712c-.13-.398-.28-.749-.455-1.02-.36-.559-.753-.795-1.09-.695-.546.161-.832 1.222-.777 2.86-.311.646-.585 1.303-.816 1.952-.098-.312-.207-.6-.333-.844-.325-.632-.706-.958-1.063-.855-.49.14-.779 1.04-.79 2.434-.308.86-.54 1.671-.688 2.393-.15.735-.212 1.377-.168 1.877.06.67.283 1.06.611.96.19-.058.35-.25.478-.548.085.282.197.48.34.524.256.077.517-.187.747-.7.083.245.192.406.333.362.263-.08.432-.604.5-1.41.347-.12.71-.26 1.086-.42.038.402.125.63.264.588.217-.066.365-.534.44-1.27.44-.22.875-.463 1.3-.728.042.362.13.565.267.524.233-.071.378-.63.422-1.504.408-.276.812-.573 1.207-.886.037.422.126.662.27.618.225-.068.377-.598.437-1.419.449-.381.874-.774 1.266-1.165.034.315.112.498.233.461.244-.074.39-.72.41-1.726a17.5 17.5 0 0 0 1.474-1.876c.02.215.076.346.168.318.233-.071.376-.697.393-1.67.37-.568.697-1.128.969-1.66.027.254.09.41.2.377.226-.069.372-.646.407-1.55.217-.46.393-.893.52-1.276.303-.913.353-1.587.066-1.5z"/>
    </svg>
  ),
  nginx: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12 0L1.605 6v12L12 24l10.395-6V6L12 0zm6 16.59c0 .705-.646 1.29-1.529 1.29-.631 0-1.351-.255-1.801-.81l-6-7.141v6.66c0 .721-.57 1.29-1.274 1.29H7.32c-.721 0-1.29-.6-1.29-1.29V7.41c0-.705.63-1.29 1.5-1.29.646 0 1.38.255 1.83.81l5.97 7.141V7.41c0-.721.6-1.29 1.29-1.29h.06c.72 0 1.29.6 1.29 1.29v9.18h.03z"/>
    </svg>
  ),
  cloudflare: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M16.509 16.516c.167-.476.082-.912-.236-1.218-.298-.286-.728-.447-1.208-.467l-10.152-.147a.188.188 0 0 1-.164-.098.19.19 0 0 1 .002-.19c.04-.07.11-.118.191-.127l10.24-.147c1.248-.056 2.601-1.078 3.078-2.318l.604-1.572a.32.32 0 0 0 .015-.178c-.826-3.89-4.297-6.795-8.436-6.795-4.72 0-8.559 3.766-8.706 8.452a3.71 3.71 0 0 0-2.64 1.076A3.58 3.58 0 0 0 0 16.073a.146.146 0 0 0 .146.165h15.95c.085 0 .16-.054.188-.135l.225-.587z"/>
      <path d="M19.785 9.061a.2.2 0 0 0-.193-.052 2.46 2.46 0 0 0-.768.289.19.19 0 0 0-.087.183 3.37 3.37 0 0 1-.068 1.273c-.437 1.56-1.81 2.695-3.413 2.823l-.09.008c-.5.04-.89.453-.89.956v.55c0 .583.399 1.092.963 1.202a9.46 9.46 0 0 0 1.89.196c3.168 0 5.986-1.57 7.704-3.974a.183.183 0 0 0-.05-.266 4.47 4.47 0 0 0-4.998-2.988z"/>
    </svg>
  ),
  "amazon cloudfront": (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 0 1-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 0 1-.287-.375 6.18 6.18 0 0 1-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.032-.863.104-.296.072-.583.16-.863.272a2.287 2.287 0 0 1-.28.104.488.488 0 0 1-.127.023c-.112 0-.168-.08-.168-.247v-.391c0-.128.016-.224.056-.28a.597.597 0 0 1 .224-.167c.279-.144.614-.264 1.005-.36a4.84 4.84 0 0 1 1.246-.152c.95 0 1.644.216 2.091.647.439.43.662 1.085.662 1.963v2.586zm-3.24 1.214c.263 0 .534-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 0 0-.735-.136 6.02 6.02 0 0 0-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.144 0-.24-.024-.304-.08-.064-.048-.12-.16-.168-.311L7.586 5.55a1.398 1.398 0 0 1-.072-.335c0-.128.064-.2.191-.2h.783c.151 0 .255.025.31.08.065.048.113.16.16.312l1.342 5.284 1.245-5.284c.04-.16.088-.264.151-.312a.549.549 0 0 1 .32-.08h.638c.152 0 .256.025.32.08.063.048.12.16.151.312l1.261 5.348 1.381-5.348c.048-.16.104-.264.16-.312a.52.52 0 0 1 .311-.08h.743c.128 0 .2.065.2.2 0 .04-.009.08-.017.128a1.137 1.137 0 0 1-.056.215l-1.923 6.17c-.048.16-.104.264-.168.312a.549.549 0 0 1-.32.08h-.687c-.152 0-.256-.024-.32-.08-.063-.056-.12-.16-.15-.32l-1.238-5.148-1.23 5.14c-.04.16-.087.264-.15.32-.065.056-.177.08-.32.08zm10.256.215c-.415 0-.83-.048-1.229-.143-.399-.096-.71-.2-.918-.32-.128-.071-.216-.151-.248-.22a.563.563 0 0 1-.048-.224v-.407c0-.167.064-.247.183-.247.048 0 .096.008.144.024.048.016.12.048.2.08.271.12.566.215.878.279.319.064.63.096.95.096.502 0 .894-.088 1.165-.264a.86.86 0 0 0 .415-.758.777.777 0 0 0-.215-.559c-.144-.151-.415-.287-.807-.414l-1.157-.36c-.583-.183-1.014-.454-1.277-.813a1.902 1.902 0 0 1-.4-1.158c0-.335.073-.63.216-.886.144-.255.335-.479.575-.654.24-.184.51-.32.83-.415.32-.096.655-.136 1.006-.136.176 0 .359.008.535.032.183.024.35.056.518.088.16.04.312.08.455.127.144.048.256.096.336.144a.69.69 0 0 1 .24.2.43.43 0 0 1 .071.263v.375c0 .168-.064.256-.184.256a.83.83 0 0 1-.303-.096 3.652 3.652 0 0 0-1.532-.311c-.455 0-.815.071-1.062.223-.248.152-.375.383-.375.71 0 .224.08.416.24.567.16.152.454.304.878.44l1.134.358c.574.184.99.44 1.237.767.248.328.375.703.375 1.118 0 .344-.072.655-.207.926-.144.272-.336.511-.583.703-.248.2-.543.343-.886.447-.36.111-.734.167-1.142.167z"/>
    </svg>
  ),
  fastly: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.5 7.5h-8v3h7v2h-7v5.5H7.5v-13h10v2.5z"/>
    </svg>
  ),
  wordpress: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M21.469 6.825c.84 1.537 1.318 3.3 1.318 5.175 0 3.979-2.156 7.456-5.363 9.325l3.295-9.527c.615-1.54.82-2.771.82-3.864 0-.397-.026-.766-.07-1.109m-7.981.105c.647-.034 1.23-.1 1.23-.1.58-.068.512-.922-.066-.89 0 0-1.745.138-2.871.138-1.06 0-2.84-.138-2.84-.138-.58-.032-.645.856-.066.89 0 0 .548.066 1.13.1l1.68 4.605-2.36 7.08L5.82 6.93c.65-.034 1.233-.1 1.233-.1.58-.068.514-.922-.065-.89 0 0-1.746.138-2.872.138-.2 0-.438-.008-.69-.02A11.862 11.862 0 0 1 12 .003c2.928 0 5.598 1.058 7.663 2.813-.048-.003-.096-.008-.145-.008-1.058 0-1.81.922-1.81 1.914 0 .89.513 1.643 1.06 2.531.41.72.89 1.643.89 2.977 0 .923-.354 1.994-.82 3.487l-1.075 3.59-3.893-11.577h.001zM12 22.784a11.83 11.83 0 0 1-3.407-.5l3.618-10.51 3.708 10.157c.024.058.052.112.084.162A11.83 11.83 0 0 1 12 22.784M.411 12c0-2.32.662-4.49 1.806-6.324l4.985 13.662C2.823 17.267 0 14.907 0 12h.411z"/>
    </svg>
  ),
  jquery: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M5.205 0L3.71 1.51C-.37 5.748-.933 12.217 2.348 17.091l.2.283L8.3 22.796C13.39 25.386 19.616 23.57 22.207 18.48c.98-1.927 1.413-4.084 1.246-6.234L5.205 0z"/>
    </svg>
  ),
  react: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M14.23 12.004a2.236 2.236 0 0 1-2.235 2.236 2.236 2.236 0 0 1-2.236-2.236 2.236 2.236 0 0 1 2.235-2.236 2.236 2.236 0 0 1 2.236 2.236zm2.648-10.69c-1.346 0-3.107.96-4.888 2.622-1.78-1.653-3.542-2.602-4.887-2.602-.41 0-.783.093-1.106.278-1.375.793-1.683 3.264-.973 6.365C1.98 8.917 0 10.42 0 12.004c0 1.59 1.99 3.097 5.043 4.03-.704 3.113-.39 5.588.988 6.38.32.187.69.275 1.102.275 1.345 0 3.107-.96 4.888-2.624 1.78 1.654 3.542 2.603 4.887 2.603.41 0 .783-.09 1.106-.275 1.374-.792 1.683-3.263.973-6.365C22.02 15.096 24 13.59 24 12.004c0-1.59-1.99-3.097-5.043-4.032.704-3.11.39-5.587-.988-6.38-.318-.184-.688-.277-1.092-.278z"/>
    </svg>
  ),
  vue: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M24 1.61h-9.94L12 5.16 9.94 1.61H0l12 20.78ZM12 14.08 5.16 2.23h4.43L12 6.41l2.41-4.18h4.43Z"/>
    </svg>
  ),
  angular: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M9.931 12.645h4.138l-2.07-4.908m0-7.737L.68 3.982l1.726 14.771L12 24l9.596-5.242L23.32 3.984 11.999.001zm6.587 18.31H14.09l-2.09-5.148H8.942L6.852 18.3H4.414L12 2.65z"/>
    </svg>
  ),
  php: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M7.01 10.207h-.944l-.515 2.648h.838c.556 0 .97-.105 1.242-.314.272-.21.455-.559.55-1.049.092-.47.05-.802-.124-.995-.175-.193-.523-.29-1.047-.29zM12 5.688C5.373 5.688 0 8.514 0 12s5.373 6.313 12 6.313S24 15.486 24 12c0-3.486-5.373-6.312-12-6.312zm-3.26 7.451c-.261.25-.575.438-.917.551-.336.108-.765.164-1.285.164H5.357l-.327 1.681H3.652l1.23-6.326h2.65c.797 0 1.378.209 1.744.628.366.418.476 1.002.33 1.752a2.836 2.836 0 0 1-.43 1.03c-.186.282-.416.508-.686.674-.15.092-.31.17-.476.232l.026-.386zm4.568-3.332l-.455 2.339c-.07.358-.044.605.079.744.122.139.386.208.79.208h.573l-.36 1.845h-1.136c-.735 0-1.264-.17-1.586-.511-.322-.34-.405-.872-.248-1.594l.455-2.339H9.865l.349-1.794h.556l.28-1.44h1.375l-.28 1.44h1.247l-.348 1.794h-1.247v-.692zm6.29.852c-.261.25-.575.438-.917.551-.336.108-.765.164-1.285.164H16.215l-.327 1.681h-1.378l1.23-6.326h2.649c.797 0 1.378.209 1.744.628.366.418.476 1.002.33 1.752a2.836 2.836 0 0 1-.43 1.03c-.186.282-.416.508-.686.674-.15.092-.31.17-.476.232l.026-.386zm-.907-1.32h-.944l-.515 2.648h.838c.557 0 .971-.105 1.242-.314.272-.21.455-.559.55-1.049.092-.47.05-.802-.124-.995-.175-.193-.523-.29-1.047-.29z"/>
    </svg>
  ),
  python: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M14.25.18l.9.2.73.26.59.3.45.32.34.34.25.34.16.33.1.3.04.26.02.2-.01.13V8.5l-.05.63-.13.55-.21.46-.26.38-.3.31-.33.25-.35.19-.35.14-.33.1-.3.07-.26.04-.21.02H8.77l-.69.05-.59.14-.5.22-.41.27-.33.32-.27.35-.2.36-.15.37-.1.35-.07.32-.04.27-.02.21v3.06H3.17l-.21-.03-.28-.07-.32-.12-.35-.18-.36-.26-.36-.36-.35-.46-.32-.59-.28-.73-.21-.88-.14-1.05-.05-1.23.06-1.22.16-1.04.24-.87.32-.71.36-.57.4-.44.42-.33.42-.24.4-.16.36-.1.32-.05.24-.01h.16l.06.01h8.16v-.83H6.18l-.01-2.75-.02-.37.05-.35.12-.33.2-.3.28-.26.37-.23.47-.2.58-.15.68-.12.78-.07.88-.03.96.01 1.02.08 1.08.16 1.12.28zm-6.42 18.3c-.38 0-.68-.3-.68-.68 0-.37.3-.68.68-.68.37 0 .68.3.68.68 0 .38-.31.68-.68.68z"/>
      <path d="M9.75 23.82l-.9-.2-.73-.26-.59-.3-.45-.32-.34-.34-.25-.34-.16-.33-.1-.3-.04-.26-.02-.2V13.5l.05-.63.13-.55.21-.46.26-.38.3-.31.33-.25.35-.19.35-.14.33-.1.3-.07.26-.04.21-.02H15.23l.69-.05.59-.14.5-.22.41-.27.33-.32.27-.35.2-.36.15-.37.1-.35.07-.32.04-.27.02-.21V5.94h3.36l.21.03.28.07.32.12.35.18.36.26.36.36.35.46.32.59.28.73.21.88.14 1.05.05 1.23-.06 1.22-.16 1.04-.24.87-.32.71-.36.57-.4.44-.42.33-.42.24-.4.16-.36.1-.32.05-.24.01h-.16l-.06-.01H9.84v.83h5.77l.01 2.75.02.37-.05.35-.12.33-.2.3-.28.26-.37.23-.47.2-.58.15-.68.12-.78.07-.88.03-.96-.01-1.02-.08-1.08-.16-1.12-.28zm6.42-18.3c.38 0 .68.3.68.68 0 .37-.3.68-.68.68-.37 0-.68-.3-.68-.68 0-.38.31-.68.68-.68z"/>
    </svg>
  ),
  "google analytics": (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M22.84 2.998v17.99a2.993 2.993 0 0 1-4.202 2.735 2.994 2.994 0 0 1-1.779-2.735v-17.99a2.99 2.99 0 0 1 5.025-2.186c.622.622.957 1.42.957 2.186zM14.49 12.003v8.985a2.99 2.99 0 0 1-5.98 0v-8.985a2.992 2.992 0 0 1 5.026-2.186c.622.622.956 1.42.955 2.186zM6.14 18.498a2.99 2.99 0 1 1-5.98 0 2.99 2.99 0 0 1 5.98 0z"/>
    </svg>
  ),
  bootstrap: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M11.77 11.24H9.956V8.202h2.152c1.17 0 1.834.522 1.834 1.466 0 1.008-.773 1.572-2.174 1.572zm.324 1.206H9.957v3.348h2.231c1.459 0 2.232-.585 2.232-1.685s-.795-1.663-2.326-1.663zM24 11.39v1.218c-1.128.108-1.817.944-2.226 2.268-.407 1.319-.463 2.937-.42 4.186.045 1.3-.968 2.5-2.337 2.5H4.985c-1.37 0-2.383-1.2-2.337-2.5.043-1.249-.013-2.867-.42-4.186-.41-1.324-1.1-2.16-2.228-2.268V11.39c1.128-.108 1.819-.944 2.227-2.268.408-1.319.464-2.937.42-4.186-.045-1.3.968-2.5 2.338-2.5h14.032c1.37 0 2.382 1.2 2.337 2.5-.043 1.249.013 2.867.42 4.186.409 1.324 1.098 2.16 2.226 2.268zm-7.927 2.817c0-1.354-.953-2.333-2.368-2.488v-.057c1.04-.169 1.856-1.135 1.856-2.213 0-1.537-1.213-2.538-3.062-2.538h-4.16v10.172h4.181c2.218 0 3.553-1.086 3.553-2.876z"/>
    </svg>
  ),
  tailwind: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12.001 4.8c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624C13.666 10.618 15.027 12 18.001 12c3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C16.337 6.182 14.976 4.8 12.001 4.8zm-6 7.2c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624 1.177 1.194 2.538 2.576 5.512 2.576 3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C10.337 13.382 8.976 12 6.001 12z"/>
    </svg>
  ),
  shopify: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104zm-1.332-18.317c-.232-.066-.48-.136-.749-.21-.216-.702-.594-1.509-1.249-2.01-.976-.744-1.969-.519-2.576.222-.739-.142-1.474-.284-2.047-.397 1.062-2.137 2.801-3.09 4.889-3.09 2.644 0 4.305 1.853 5.015 3.71-.428-.108-.881-.222-1.355-.341l-.006.03c-.098-.025-.196-.05-.298-.076-.072-.636-.312-1.275-.798-1.716-.737-.666-1.642-.42-2.123.204-.362.472-.57 1.107-.661 1.807-.543-.14-1.111-.29-1.682-.441.211-.968.651-1.89 1.38-2.41.43-.306 1.027-.412 1.529-.093.484.308.719.907.845 1.558.027.005.05.013.076.019.16-.012.323-.012.488.001z"/>
    </svg>
  ),
  drupal: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M15.78 5.113C14.09 3.425 12.48 1.815 11.998 0c-.48 1.815-2.09 3.425-3.778 5.113-2.534 2.53-5.405 5.4-5.405 9.702a9.184 9.184 0 1 0 18.368 0c0-4.303-2.871-7.171-5.403-9.702zM6.72 16.954c-.563-.019-2.64-3.6 1.215-7.416l2.55 2.788a.218.218 0 0 1-.016.325c-1.542 1.274-3.206 4.322-3.75 4.303zM12 21.677a3.158 3.158 0 0 1-3.158-3.159 3.29 3.29 0 0 1 .787-2.08c.66-.752 1.529-1.392 2.371-2.071.852.686 1.681 1.319 2.371 2.08a3.29 3.29 0 0 1 .787 2.08A3.158 3.158 0 0 1 12 21.677zm5.89-5.963c-.661-.018-1.76-1.517-2.565-2.929-.313-.554-.632-1.12-.898-1.423L12.009 8.97l-2.39 2.425c-.27.3-.593.865-.903 1.412-.793 1.39-1.87 3.397-2.553 3.344C5.6 16.098 5.6 15.134 5.6 14.815c0-3.508 2.473-5.98 4.661-8.17.405-.406.849-.85 1.308-1.343.192-.193.489-.483.713-.708.225.228.528.523.711.707.461.49.905.937 1.311 1.343 2.189 2.19 4.664 4.662 4.664 8.171 0 .319 0 1.285-.567 1.339-.18.015-.36-.013-.511-.08z"/>
    </svg>
  ),
  joomla: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M6.75 9.344l3.028 3.028-3.03 3.03a4.267 4.267 0 0 1-.083 5.936 4.263 4.263 0 0 1-5.996-.088c-1.077-1.077-1.453-2.593-1.132-3.988l.097.079a5.48 5.48 0 0 0 2.51 1.229c.382 1.032 1.456 1.664 2.608 1.387.57-.137 1.05-.483 1.355-.966l3.045-3.044-3.028-3.028c-.37-.37-.466-1.054-.112-1.446L6.75 9.344zM20.916 3.084c1.65 1.65 1.65 4.347-.088 5.996a4.29 4.29 0 0 1-2.006 1.162l-.072-.09a5.48 5.48 0 0 0-1.13-2.545c-.962-.4-2.131-.219-2.893.543l-3.03 3.03-3.027-3.03c-.37-.37-.466-1.054-.112-1.446l5.169-5.168a4.275 4.275 0 0 1 5.936-.088 4.299 4.299 0 0 1 1.253 1.636zM9.346 17.25l3.03-3.03 3.027 3.03c.37.369.466 1.053.112 1.445l-5.168 5.168a4.267 4.267 0 0 1-5.936.088 4.303 4.303 0 0 1 .835-6.7l.083.098a5.482 5.482 0 0 0 1.173 2.35c.941.495 2.12.348 2.904-.436l3.045-3.044-3.03-3.03c-.37-.37-.466-1.054-.112-1.446L9.346 17.25zm11.57-11.34a4.303 4.303 0 0 1-.835 6.701l-.083-.098a5.482 5.482 0 0 0-1.174-2.35c-.94-.496-2.12-.349-2.903.436l-3.045 3.044 3.03 3.03c.37.37.466 1.053.112 1.445l-5.168-5.168 3.03-3.03a4.267 4.267 0 0 1 5.036-1.009z"/>
    </svg>
  ),
  varnish: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12 0L2 4v16l10 4 10-4V4L12 0zm0 2.18l7.83 3.13L12 8.44 4.17 5.31 12 2.18zM3 6.46l8.5 3.4V21.3L3 17.65V6.46zm17 0v11.19l-8.5 3.65V9.86L21 6.46z"/>
    </svg>
  ),
  iis: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M0 0v24h24V0H0zm3.7 3.98h4.553v1.707H6.3v6.566H8.02v1.707H3.7V12.253h1.72V5.687H3.7V3.98zm6.387 0h4.553v1.707h-1.953v6.566h1.72v1.707h-4.32V12.253h1.72V5.687H10.087V3.98zm6.392 0h4.553v1.707h-1.954v6.566h1.721v1.707h-4.32V12.253h1.72V5.687h-1.72V3.98zM3.285 16.033h17.43v3.987H3.285v-3.987z"/>
    </svg>
  ),
  vercel: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M24 22.525H0l12-21.05 12 21.05z"/>
    </svg>
  ),
  netlify: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M6.49 19.04h-.23L5.13 17.9v-.23l1.73-1.71h1.2l.15.15v1.2L6.49 19.04zM5.13 6.31V6.1l1.13-1.13h.23L8.2 6.66v1.2l-.14.15H6.87L5.13 6.31zm12.74 6.47l-1.74-1.74.58-.58 2.35.57.57 2.33-.58.58-1.18-1.16zM5.13 12.53v-.24l2.61-2.61h.23l1.68 1.68v.23l-1.68 1.68h-.23L5.13 12.53zm6.38 6.32h-.24l-1.52-1.5V16.3l.14-.14h1.2l1.56 1.55v.23l-1.14 1.1zm0-13.7h-.24l-1.14-1.14v-.22L11.27 2.3h.24l1.55 1.56v1.15l-.14.14H11.51zM2.1 10.12l-.58-.58.57-2.35 2.35-.57.58.58-1.16 1.18-1.76 1.74zm2.34 7.28l-2.35-.57-.57-.58.58-.58 1.74 1.74 1.18 1.16-.58.58-.57-2.35V17.4zm15.22-7.28l1.76-1.74 1.16-1.18-.58-.58-2.35.57-.57 2.35.58.58zM17.3 19.04h-.23l-1.73-1.73v-1.2l.15-.15h1.2l1.73 1.73v.23l-1.12 1.12zm.67-4.89l-1.68-1.68v-.23l1.68-1.68h.24l2.61 2.61v.24l-2.61 2.61h-.24v-.87zm-5.45 8.21h-.24l-.58-.57 1.76-1.76 1.18-1.16.58.58-.57 2.35-2.13.56zm-.24-20.72h.24l2.13.57.57 2.35-.58.58-1.18-1.16-1.76-1.76.58-.58zm-7 15.32V16.3l.14-.14h1.2l1.73 1.73v.23l-1.55 1.55h-.24l-1.28-1.32zm10.46-5.87v-.23l1.55-1.52h.23l1.32 1.28v.23l-.14.14h-1.2l-1.76-1.64v1.74zm-1.46-4.16l-.15-.14V5.63L17.3 3.98h.23L18.66 5.1v.23L14.96 9.03h-.24l-1.44-1.1zM6.48 3.98h.23l3.13 3.1.14.15v1.2L8.46 9.95h-.23L5.13 6.82V6.1l1.35-2.12z"/>
    </svg>
  ),
  "next.js": (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M11.572 0c-.176.001-.215.002-.358.007a19.76 19.76 0 0 0-.364.025c-.225.02-.45.053-.672.097a7.86 7.86 0 0 0-.622.166 8.04 8.04 0 0 0-.582.22c-.274.117-.464.212-.694.34-.234.13-.53.32-.736.464A12.04 12.04 0 0 0 .295 12.174a12.024 12.024 0 0 0 3.285 8.317c.043.046.086.09.13.135l9.844-12.293V18.48c0 .065.002.13.013.193a.78.78 0 0 0 .224.447.779.779 0 0 0 .64.217h.004V6.543L19.481 0H11.572zm7.685 20.853a12.06 12.06 0 0 1-5.792 2.987 12.06 12.06 0 0 1-6.573-.544A12.024 12.024 0 0 1 .295 12.174a12.014 12.014 0 0 1 .816-4.369l12.469 17.348c.036-.05.07-.103.098-.16.028-.058.042-.12.05-.183v-5.61l4.528 1.653z"/>
    </svg>
  ),
  nuxt: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M13.4 22H3.6c-.3 0-.5 0-.8-.1-.2-.1-.5-.2-.6-.4-.2-.2-.3-.4-.4-.6-.1-.3-.1-.5-.1-.8 0-.2.1-.5.2-.7l6.8-11.8c.1-.2.3-.4.5-.5.2-.1.4-.2.7-.2.2 0 .5.1.7.2.2.1.3.3.5.5l6.8 11.8c.1.2.2.5.2.7 0 .3 0 .5-.1.8-.1.2-.2.5-.4.6-.2.2-.4.3-.6.4-.3.1-.5.1-.8.1z"/>
      <path d="M21.2 22h-4.5l-5.5-9.5L14.8 7c.1-.2.3-.4.5-.5.2-.1.4-.2.7-.2.2 0 .5.1.7.2.2.1.3.3.5.5l6.8 11.8c.1.2.2.5.2.7 0 .3 0 .5-.1.8-.1.2-.2.5-.4.6-.2.2-.4.3-.6.4-.3.1-.6.1-.9.1z"/>
    </svg>
  ),
  akamai: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm4.94 17.47c-2.1 1.03-4.87 1.53-7.81.77 0 0-.52-.15-.95-.32-.11-.04-.13-.13-.03-.17l1.63-.73c.1-.04.19-.04.29 0 .99.39 2.09.55 3.33.39 2.02-.27 3.5-1.31 4.33-2.63.17-.27.32-.56.44-.87.04-.1.13-.13.19-.06.91 1.07.24 2.61-1.42 3.62z"/>
    </svg>
  ),
  redis: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M10.5 2.661l.54.997-1.797.644 2.409.218.748 1.246.467-1.121 2.077-.208-1.61-.613.466-1.053-1.278.503zm4.2-1.039l1.095 1.94 3.097-.994-2.06 2.572 1.14 2.14-2.77-1.27-2.263 2.26.535-3.153-2.553-1.394 3.196-.462zM24 7.893c0 1.107-2.865 2.093-7.26 2.53l-1.024.092-.534.864c-1.085 1.756-2.168 2.9-3.182 2.9-1.014 0-2.097-1.144-3.182-2.9l-.534-.863-1.024-.093C2.865 9.986 0 9 0 7.894c0-.776 1.2-1.47 3.14-1.996l.753-.162.324-.712C5.47 2.243 7.604.006 9.267.006c.784 0 1.56.431 2.301 1.2l.432.446.432-.446c.74-.769 1.517-1.2 2.3-1.2 1.664 0 3.798 2.237 5.052 5.018l.323.712.753.163c1.94.525 3.14 1.22 3.14 1.994zM12 14.92c2.07-1.394 3.778-3.476 4.746-5.28C19.8 9.2 22 8.48 22 7.893c0-.353-.723-.834-2.005-1.237-.67 2.46-1.773 4.74-3.087 6.097zm-4.746-5.28C8.222 11.444 9.93 13.526 12 14.92l-4.908-2.164c-1.314-1.357-2.418-3.636-3.087-6.097C2.723 7.06 2 7.54 2 7.894c0 .587 2.2 1.307 5.254 1.747z"/>
    </svg>
  ),
  mysql: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M16.405 5.501c-.115 0-.193.014-.274.033v.013h.014c.054.104.146.18.214.273.054.107.1.214.154.32l.014-.015c.094-.066.14-.172.14-.333-.04-.047-.046-.094-.08-.14-.04-.067-.126-.1-.18-.153zM5.77 18.695h-.927a50.854 50.854 0 0 0-.27-4.41h-.008l-1.41 4.41H2.45l-1.4-4.41h-.01a72.892 72.892 0 0 0-.195 4.41H0c.055-1.966.192-3.81.41-5.53h1.15l1.335 4.064h.008l1.347-4.064h1.095c.242 2.015.384 3.86.428 5.53zM7.542 18.695h-.835c.06-.714.095-1.425.11-2.134.013-.71.02-1.422.02-2.135h.835c0 .713-.006 1.425-.02 2.135-.013.709-.049 1.42-.11 2.134zM10.804 14.107c-.2-.038-.44-.06-.72-.06-.492 0-.877.14-1.155.42-.278.28-.417.673-.417 1.18 0 .46.103.822.312 1.087.208.265.498.397.872.397.36 0 .654-.13.884-.388.23-.258.345-.598.345-1.02 0-.522-.107-.89-.32-1.107l-.001-.001v-.508z"/>
    </svg>
  ),
  postgresql: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M23.556 14.263c-.208-.716-.901-1.111-1.56-.961l-.285.067a10.08 10.08 0 0 1-.68-2.697 16.57 16.57 0 0 0-.333-1.767c-.226-.85-.506-1.33-.792-1.548.018-.04.041-.073.057-.115l.156-.408a6.37 6.37 0 0 0 .281-1.16c.066-.443.073-.826-.009-1.17a1.817 1.817 0 0 0-.618-.945c-.386-.329-.875-.487-1.37-.515-.482-.027-1.06.065-1.39.167l-.19.06a8.598 8.598 0 0 0-.775-.567 7.06 7.06 0 0 0-1.775-.808c-.536-.17-1.08-.263-1.621-.291v-.013c-.005-.004.004-.017 0-.022a4.446 4.446 0 0 0-.29-1.052A3.27 3.27 0 0 0 11.793.37a3.726 3.726 0 0 0-1.455-.33C9.767 0 9.249.12 8.808.32c-.603.273-1.04.688-1.37 1.16-.186.264-.33.545-.436.839-.043.116-.076.234-.106.353-.145.003-.289.012-.434.03-.524.064-1.048.206-1.537.437a5.006 5.006 0 0 0-1.6 1.196c-.386.416-.652.8-.862 1.254a5.89 5.89 0 0 0-.466 1.762 6.208 6.208 0 0 0 .072 2.013c.054.293.136.58.24.862-.135.336-.233.677-.29 1.028-.078.488-.078.99.025 1.511.052.27.127.537.234.772l.006.015c-.137.33-.234.67-.29 1.022-.074.47-.075.955.016 1.471.09.517.263 1.054.548 1.539.274.465.657.854 1.11 1.118.387.225.818.375 1.314.406 1.055 3.095 3.065 4.67 5.898 5.23.44.088.893.137 1.35.147 1.257.029 2.535-.237 3.608-.823.57-.311 1.063-.717 1.454-1.206.025-.031.045-.063.069-.095a4.77 4.77 0 0 0 1.104.136c.08.003.164.005.244.002a4.28 4.28 0 0 0 1.533-.354c.437-.2.828-.47 1.11-.818a2.13 2.13 0 0 0 .423-1.085 7.398 7.398 0 0 0-.039-.967l-.016-.178a7.62 7.62 0 0 0-.072-.56l-.003-.021.086-.044c.66-.342 1.09-.8 1.344-1.338.254-.536.336-1.156.261-1.835a5.99 5.99 0 0 0-.277-1.14l-.006-.017z"/>
    </svg>
  ),
  heroku: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M20.61 0H3.39C2.189 0 1.23.96 1.23 2.16v19.681C1.23 23.04 2.189 24 3.39 24h17.22c1.2 0 2.159-.96 2.159-2.159V2.16C22.77.96 21.81 0 20.61 0zm.96 21.841c0 .539-.421.96-.96.96H3.39c-.54 0-.96-.421-.96-.96V2.16c0-.54.42-.961.96-.961h17.22c.539 0 .96.421.96.961v19.681zM6.27 20.16l3.54-3.54-3.54-3.539V20.16zm8.19-15.84c-1.2-.78-2.34-1.08-4.2-1.08H6.27v7.5h3.99c1.86 0 3.12-.24 4.17-1.08 1.08-.84 1.74-2.16 1.74-3.66.06-1.5-.6-2.82-1.74-3.68zM12 10.5H9.27V6.24H12c1.68 0 2.88.84 2.88 2.16 0 1.32-1.2 2.1-2.88 2.1zm5.73 1.5c-.48-.54-1.56-1.14-2.88-1.44 1.32-.36 2.46-.96 3.12-2.16h-1.68c-.54.84-1.56 1.38-2.82 1.68v1.08c1.56.18 2.76.72 3.48 1.44.78.78 1.08 1.86 1.08 3.12v4.44h-2.04v-4.32c0-1.32-.36-2.46-1.26-3.36v-.48z"/>
    </svg>
  ),
  woocommerce: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M2.227 4.857A2.228 2.228 0 0 0 0 7.094v7.457c0 1.236 1.001 2.237 2.237 2.237h3.09l-.705 2.282 3.453-2.282h4.818c1.236 0 2.237-1.001 2.237-2.237V7.094c0-1.236-1.001-2.237-2.237-2.237H2.227z"/>
    </svg>
  ),
  magento: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12 0L2.4 5.4v13.2L6 20.4V7.2l6-3.39 6 3.39v13.2l3.6-1.8V5.4L12 0zm0 15.6L9.6 14.4v-5.7L12 7.5l2.4 1.2v5.7L12 15.6z"/>
    </svg>
  ),
};

// Variations mapping for flexible matching
const TechIconVariations: Record<string, string> = {
  "amazon cloudfront": "amazon cloudfront",
  cloudfront: "amazon cloudfront",
  "aws cloudfront": "amazon cloudfront",
  tailwindcss: "tailwind",
  "tailwind css": "tailwind",
  reactjs: "react",
  "react.js": "react",
  "vue.js": "vue",
  vuejs: "vue",
  angularjs: "angular",
  "next.js": "next.js",
  nextjs: "next.js",
  "nuxt.js": "nuxt",
  nuxtjs: "nuxt",
  "microsoft-iis": "iis",
  "microsoft iis": "iis",
  "php/": "php",
};

function getTechIcon(name: string): React.ReactNode | null {
  const key = name.toLowerCase();
  if (TechIcons[key]) return TechIcons[key];
  if (TechIconVariations[key] && TechIcons[TechIconVariations[key]]) {
    return TechIcons[TechIconVariations[key]];
  }
  // partial match
  for (const [iconKey, icon] of Object.entries(TechIcons)) {
    if (key.includes(iconKey) || iconKey.includes(key)) return icon;
  }
  return null;
}

// ─── Display Name Normalization ──────────────────────────────────────────────
const DISPLAY_NAMES: Record<string, string> = {
  "amazon cloudfront": "Amazon CloudFront",
  "cloudfront": "Amazon CloudFront",
  "cloudflare": "Cloudflare",
  "fastly": "Fastly",
  "akamai": "Akamai",
  "nginx": "Nginx",
  "apache": "Apache",
  "iis": "IIS",
  "microsoft-iis": "Microsoft IIS",
  "microsoft iis": "Microsoft IIS",
  "wordpress": "WordPress",
  "drupal": "Drupal",
  "joomla": "Joomla!",
  "woocommerce": "WooCommerce",
  "magento": "Magento",
  "shopify": "Shopify",
  "jquery": "jQuery",
  "react": "React",
  "vue": "Vue.js",
  "vue.js": "Vue.js",
  "angular": "Angular",
  "angularjs": "AngularJS",
  "next.js": "Next.js",
  "nextjs": "Next.js",
  "nuxt": "Nuxt",
  "nuxt.js": "Nuxt.js",
  "svelte": "Svelte",
  "bootstrap": "Bootstrap",
  "tailwind": "Tailwind CSS",
  "tailwindcss": "Tailwind CSS",
  "tailwind css": "Tailwind CSS",
  "php": "PHP",
  "python": "Python",
  "ruby": "Ruby",
  "java": "Java",
  "node.js": "Node.js",
  "nodejs": "Node.js",
  "express": "Express",
  "expressjs": "Express",
  "laravel": "Laravel",
  "django": "Django",
  "rails": "Ruby on Rails",
  "ruby on rails": "Ruby on Rails",
  "mysql": "MySQL",
  "postgresql": "PostgreSQL",
  "redis": "Redis",
  "mongodb": "MongoDB",
  "varnish": "Varnish",
  "google analytics": "Google Analytics",
  "google tag manager": "Google Tag Manager",
  "matomo": "Matomo",
  "vercel": "Vercel",
  "netlify": "Netlify",
  "heroku": "Heroku",
  "aws": "AWS",
  "firebase": "Firebase",
  "typescript": "TypeScript",
  "webpack": "Webpack",
  "asp.net": "ASP.NET",
  "cpanel": "cPanel",
  "plesk": "Plesk",
  "grafana": "Grafana",
  "jenkins": "Jenkins",
  "gitlab": "GitLab",
};

function formatTechName(name: string): string {
  const normalized = DISPLAY_NAMES[name.toLowerCase()];
  if (normalized) return normalized;
  // Title case fallback for unknown names
  return name.replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Data Types ──────────────────────────────────────────────────────────────
interface Technology {
  name: string;
  category?: string;
  version?: string;
  confidence?: "high" | "medium" | "low";
  cpe?: string;
  website?: string;
  detection_method?: string;
  implies?: string[];
  eol_status?: "eol" | "current" | "unknown";
  eol_date?: string;
  cves?: string[];
  version_risk?: "outdated" | "current" | "unknown";
}

interface ServerHeaders {
  server?: string;
  x_powered_by?: string;
  x_aspnet_version?: string;
  x_generator?: string;
}

interface SubdomainTechResult {
  subdomain: string;
  technologies: Technology[];
  reachable: boolean;
}

export interface TechModuleData {
  score?: number;
  target?: string;
  technologies?: Technology[];
  headers?: ServerHeaders;
  issues?: Array<string | Issue>;
  subdomain_results?: SubdomainTechResult[];
}

/** Cloud context enrichment from ASN module (optional) */
export interface CloudContext {
  cloudProviders?: string[];
  cdnProviders?: string[];
  hostingProvider?: string;
  subdomainProviders?: Array<{ subdomain: string; providers: string[] }>;
}

export interface TechModuleViewProps {
  data: TechModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  hideHeader?: boolean;
  /** Optional cloud context from ASN module to enrich hosting/CDN info */
  cloudContext?: CloudContext;
}

// ─── Category Config ─────────────────────────────────────────────────────────
const CATEGORY_ICONS: Record<string, typeof Code> = {
  cms: Globe,
  framework: Layers,
  language: Code,
  "web server": Server,
  server: Server,
  database: Database,
  security: Shield,
  analytics: Globe,
  cdn: Globe,
  "css framework": Layers,
  "js library": Code,
  cache: Database,
  caching: Database,
  hosting: Globe,
  ecommerce: Package,
  "e-commerce": Package,
  default: Package,
};

const CATEGORY_COLORS: Record<string, string> = {
  cms: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  framework: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  language: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  "web server": "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  server: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  database: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  security: "bg-red-500/10 text-red-400 border-red-500/30",
  analytics: "bg-pink-500/10 text-pink-400 border-pink-500/30",
  cdn: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  "css framework": "bg-sky-500/10 text-sky-400 border-sky-500/30",
  "js library": "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  cache: "bg-teal-500/10 text-teal-400 border-teal-500/30",
  caching: "bg-teal-500/10 text-teal-400 border-teal-500/30",
  hosting: "bg-violet-500/10 text-violet-400 border-violet-500/30",
  ecommerce: "bg-lime-500/10 text-lime-400 border-lime-500/30",
  "e-commerce": "bg-lime-500/10 text-lime-400 border-lime-500/30",
};

// Detection method labels
function formatDetectionMethod(method?: string): string {
  if (!method) return "";
  const labels: Record<string, string> = {
    header: "HTTP Header",
    headers: "HTTP Headers",
    meta_tag: "Meta Tag",
    meta_tags: "Meta Tags",
    html_pattern: "HTML Pattern",
    js_pattern: "JS Pattern",
    cookie: "Cookie",
    script_src: "Script Source",
    css: "CSS",
    url_pattern: "URL Pattern",
    favicon: "Favicon",
    dns: "DNS",
    wappalyzer: "Wappalyzer",
  };
  return labels[method.toLowerCase()] || method.replace(/_/g, " ");
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

function ConfidenceBadge({ confidence }: { confidence?: string }) {
  const colors: Record<string, string> = {
    high: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    medium: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    low: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  };
  return (
    <Badge variant="outline" className={cn("text-xs", colors[confidence || "low"] || colors.low)}>
      {confidence || "low"}
    </Badge>
  );
}

function exportTechCsv(technologies: Technology[], moduleName: string) {
  const headers = ["Name", "Category", "Version", "Version Risk", "EOL Status", "EOL Date", "CVEs", "CPE", "Confidence", "Detection Method", "Website"];
  const csvContent = [
    headers.join(","),
    ...technologies.map((tech) => [
      `"${formatTechName(tech.name)}"`,
      `"${tech.category || ""}"`,
      `"${tech.version || ""}"`,
      `"${tech.version_risk || "unknown"}"`,
      `"${tech.eol_status || "unknown"}"`,
      `"${tech.eol_date || ""}"`,
      `"${(tech.cves || []).join("; ")}"`,
      `"${tech.cpe || ""}"`,
      `"${tech.confidence || "low"}"`,
      `"${tech.detection_method || ""}"`,
      `"${tech.website || ""}"`,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${moduleName}_inventory_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Server Info Card ────────────────────────────────────────────────────────
function ServerInfoCard({ headers }: { headers?: ServerHeaders }) {
  if (!headers) return null;
  const entries = Object.entries(headers).filter(([, v]) => v);
  if (entries.length === 0) return null;

  return (
    <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/30">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-amber-400" />
        <span className="font-medium text-amber-400 text-sm">Server Information Disclosure</span>
      </div>
      <div className="space-y-2">
        {headers.server && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Server</span>
            <code className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded">{headers.server}</code>
          </div>
        )}
        {headers.x_powered_by && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">X-Powered-By</span>
            <code className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded">{headers.x_powered_by}</code>
          </div>
        )}
        {headers.x_aspnet_version && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">ASP.NET Version</span>
            <code className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded">{headers.x_aspnet_version}</code>
          </div>
        )}
        {headers.x_generator && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Generator</span>
            <code className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded">{headers.x_generator}</code>
          </div>
        )}
      </div>
    </div>
  );
}

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "low";
      if (issue.toLowerCase().includes("outdated") || issue.toLowerCase().includes("vulnerable")) {
        severity = "high";
      } else if (issue.toLowerCase().includes("exposes") || issue.toLowerCase().includes("disclosure")) {
        severity = "medium";
      }
      return { id: `tech-issue-${index}`, severity, message: issue };
    }
    return issue;
  });
}

// ─── Main Component ──────────────────────────────────────────────────────────
export function TechModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
  cloudContext,
}: TechModuleViewProps) {
  const [categoryFilter, setCategoryFilter] = React.useState<string>("all");
  const issues = normalizeIssues(data.issues);
  const technologies = data.technologies || [];

  const categories = React.useMemo(() => {
    const cats = new Set(technologies.map((t) => t.category || "Other"));
    return Array.from(cats).sort();
  }, [technologies]);

  const filteredTechnologies = React.useMemo(() => {
    if (categoryFilter === "all") return technologies;
    return technologies.filter((t) => (t.category || "Other") === categoryFilter);
  }, [technologies, categoryFilter]);

  // Build reverse index: tech name → hosts where it was found
  const techHostMap = React.useMemo(() => {
    const map = new Map<string, string[]>();
    const target = data.target?.toLowerCase();

    // Main domain techs always include target
    if (target) {
      for (const tech of technologies) {
        const key = tech.name.toLowerCase();
        map.set(key, [target]);
      }
    }

    // Add subdomains where each tech was also found
    if (data.subdomain_results?.length) {
      for (const sub of data.subdomain_results) {
        // Skip subdomains with no technologies (unreachable or empty)
        if (!sub.technologies?.length) continue;
        for (const tech of sub.technologies) {
          const key = tech.name.toLowerCase();
          if (!map.has(key)) map.set(key, []);
          const hosts = map.get(key)!;
          if (!hosts.includes(sub.subdomain)) {
            hosts.push(sub.subdomain);
          }
        }
      }
    }

    return map;
  }, [data.subdomain_results, data.target, technologies]);

  const withVersion = technologies.filter((t) => t.version).length;
  const eolCount = technologies.filter((t) => t.eol_status === "eol").length;
  const outdatedCount = technologies.filter((t) => t.version_risk === "outdated").length;
  const cveCount = technologies.filter((t) => t.cves && t.cves.length > 0).length;

  // Table column definitions
  const columns: ColumnDef<Technology>[] = React.useMemo(() => [
    {
      id: "name",
      header: "Technology",
      accessor: (row) => formatTechName(row.name),
      sortable: true,
      filterable: true,
      cell: (_value, row) => {
        const displayName = formatTechName(row.name);
        const productIcon = getTechIcon(row.name);
        const category = row.category?.toLowerCase() || "default";
        const colorClass = CATEGORY_COLORS[category] || "bg-muted text-muted-foreground";
        const FallbackIcon = CATEGORY_ICONS[category] || CATEGORY_ICONS.default;

        // Cloud context enrichment for hosting/CDN
        const cloudServices = cloudContext && (row.category?.toLowerCase() === "hosting" || row.category?.toLowerCase() === "cdn")
          ? (row.category?.toLowerCase() === "cdn" ? cloudContext.cdnProviders : cloudContext.cloudProviders)
          : undefined;

        return (
          <div className="flex items-center gap-2.5 py-0.5">
            <div className={cn("p-1.5 rounded border flex-shrink-0", colorClass)}>
              {productIcon || <FallbackIcon className="h-3.5 w-3.5" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-sm">{displayName}</span>
                {row.website && (
                  <a href={row.website} target="_blank" rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-cyan-400 transition-colors">
                    <Link2 className="h-3 w-3" />
                  </a>
                )}
              </div>
              {row.implies && row.implies.length > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  implies {row.implies.join(", ")}
                </span>
              )}
              {cloudServices && cloudServices.length > 0 && (
                <div className="flex items-center gap-1 mt-0.5">
                  {cloudServices.map((svc, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] py-0 h-4 bg-cyan-500/10 border-cyan-500/30 text-cyan-400">
                      {svc}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: "category",
      header: "Category",
      accessor: (row) => row.category || "Other",
      sortable: true,
      filterable: true,
      cell: (value) => {
        const cat = String(value).toLowerCase();
        const colorClass = CATEGORY_COLORS[cat] || "bg-muted text-muted-foreground";
        return (
          <Badge variant="outline" className={cn("text-[11px]", colorClass)}>
            {String(value)}
          </Badge>
        );
      },
    },
    {
      id: "found_on",
      header: "Found On",
      accessor: (row) => {
        const hosts = techHostMap.get(row.name.toLowerCase());
        return hosts ? hosts.join(", ") : "";
      },
      sortable: true,
      filterable: true,
      cell: (_value, row) => {
        const hosts = techHostMap.get(row.name.toLowerCase());
        if (!hosts || hosts.length === 0) {
          return <span className="text-muted-foreground text-xs">—</span>;
        }
        return (
          <div className="flex flex-col gap-0.5">
            {hosts.map((host) => (
              <code key={host} className="text-[11px] font-mono text-cyan-400 truncate max-w-[200px]" title={host}>
                {host}
              </code>
            ))}
          </div>
        );
      },
    },
    {
      id: "version",
      header: "Version",
      accessor: (row) => row.version || "",
      sortable: true,
      cell: (_value, row) => {
        if (!row.version) return <span className="text-muted-foreground text-xs">—</span>;
        const isEol = row.eol_status === "eol";
        const isOutdated = row.version_risk === "outdated";
        return (
          <div className="flex items-center gap-1.5">
            <code className={cn(
              "text-xs font-mono",
              isEol && "text-red-400",
              isOutdated && !isEol && "text-amber-400",
              !isEol && !isOutdated && "text-foreground",
            )}>
              v{row.version}
            </code>
            {isEol && (
              <span className="text-red-400" title={row.eol_date ? `EOL: ${row.eol_date}` : "End of Life"}>
                <AlertOctagon className="h-3 w-3" />
              </span>
            )}
            {isOutdated && !isEol && (
              <span className="text-amber-400" title="Outdated version">
                <AlertTriangle className="h-3 w-3" />
              </span>
            )}
            {row.version_risk === "current" && (
              <span className="text-emerald-400" title="Current version">
                <CheckCircle2 className="h-3 w-3" />
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "detection",
      header: "Detection",
      accessor: (row) => row.detection_method || "",
      sortable: true,
      cell: (value) => {
        if (!value) return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Eye className="h-3 w-3 flex-shrink-0" />
            {formatDetectionMethod(String(value))}
          </span>
        );
      },
    },
    {
      id: "confidence",
      header: "Confidence",
      accessor: (row) => row.confidence || "low",
      sortable: true,
      cell: (value) => <ConfidenceBadge confidence={String(value)} />,
    },
    {
      id: "cves",
      header: "CVEs",
      accessor: (row) => (row.cves || []).length,
      sortable: true,
      align: "center",
      cell: (_value, row) => {
        const displayName = formatTechName(row.name);
        const nvdUrl = `https://nvd.nist.gov/vuln/search/results?form_type=Basic&results_type=overview&query=${encodeURIComponent(displayName)}&search_type=all`;

        if (!row.cves || row.cves.length === 0) {
          return (
            <a href={nvdUrl} target="_blank" rel="noopener noreferrer"
              className="text-muted-foreground hover:text-cyan-400 transition-colors inline-flex justify-center">
              <ExternalLink className="h-3 w-3" />
            </a>
          );
        }
        return (
          <a href={nvdUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-red-400 hover:text-red-300 transition-colors">
            <Shield className="h-3 w-3" />
            {row.cves.length}
          </a>
        );
      },
    },
  ], [cloudContext, techHostMap]);

  return (
    <ModuleContainer
      moduleName="tech"
      icon={Code}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className="bg-card/50">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground mb-1">Technologies</p>
              <p className="text-2xl font-bold font-mono">{technologies.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground mb-1">Categories</p>
              <p className="text-2xl font-bold font-mono">{categories.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground mb-1">With Version</p>
              <p className="text-2xl font-bold font-mono text-cyan-400">{withVersion}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <AlertOctagon className="h-3 w-3 text-red-400" /> End of Life
              </p>
              <p className={cn("text-2xl font-bold font-mono", eolCount > 0 ? "text-red-400" : "text-emerald-400")}>
                {eolCount}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-400" /> Outdated
              </p>
              <p className={cn("text-2xl font-bold font-mono", outdatedCount > 0 ? "text-amber-400" : "text-emerald-400")}>
                {outdatedCount}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Shield className="h-3 w-3 text-red-400" /> With CVEs
              </p>
              <p className={cn("text-2xl font-bold font-mono", cveCount > 0 ? "text-red-400" : "text-emerald-400")}>
                {cveCount}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filter + Export */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat} ({technologies.filter((t) => (t.category || "Other") === cat).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categoryFilter !== "all" && (
              <Badge variant="outline" className="text-xs">
                Showing {filteredTechnologies.length} of {technologies.length}
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => exportTechCsv(technologies, "tech")} className="gap-1.5">
            <Download className="h-4 w-4" />
            Export Inventory
          </Button>
        </div>

        {/* Server Headers Warning */}
        <ServerInfoCard headers={data.headers} />

        {/* Technologies Table */}
        <ModuleDataTable
          data={filteredTechnologies}
          columns={columns}
          defaultPageSize={25}
          searchable
          searchPlaceholder="Search technologies..."
          emptyMessage="No technologies detected"
          getRowId={(row, index) => `${row.name}-${index}`}
        />

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

TechModuleView.displayName = "TechModuleView";
